import { useQuery } from "@tanstack/react-query";

import {
  DashboardRequestError,
  dashboardRequest,
} from "../../providers/aws/client";
import {
  parseIdentityInspector,
  parseIdentityManifestHistory,
  parseIdentityReport,
  type IdentityInspector,
  type IdentityManifestHistory,
  type IdentityReport,
  type SignedIdentityArtifact,
} from "./identityWorkbenchContracts";

export class IdentityArtifactFetchError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number | null = null,
  ) {
    super(code);
    this.name = "IdentityArtifactFetchError";
  }
}

function retryDashboard(failureCount: number, error: Error) {
  if (
    error instanceof DashboardRequestError &&
    [401, 403, 404].includes(error.status)
  ) {
    return false;
  }
  return failureCount < 2;
}

async function sha256(value: ArrayBuffer) {
  const digest = await crypto.subtle.digest("SHA-256", value);
  return [...new Uint8Array(digest)]
    .map((item) => item.toString(16).padStart(2, "0"))
    .join("");
}

export async function fetchIdentityArtifactBytes(
  artifact: SignedIdentityArtifact,
  signal?: AbortSignal,
) {
  const response = await fetch(artifact.url, {
    signal,
    credentials: "omit",
    referrerPolicy: "no-referrer",
  });
  if (!response.ok) {
    throw new IdentityArtifactFetchError(
      response.status === 401 || response.status === 403
        ? "identity_artifact_expired"
        : "identity_artifact_unavailable",
      response.status,
    );
  }
  const value = await response.arrayBuffer();
  if (value.byteLength !== artifact.byte_size) {
    throw new IdentityArtifactFetchError("identity_artifact_size_mismatch");
  }
  const observed = `sha256:${await sha256(value)}`;
  if (observed !== artifact.content_digest) {
    throw new IdentityArtifactFetchError("identity_artifact_digest_mismatch");
  }
  return value;
}

export async function fetchIdentityManifestHistory(
  sessionId: string,
  signal?: AbortSignal,
): Promise<IdentityManifestHistory> {
  const response = await dashboardRequest<unknown>(
    `/video-sessions/${encodeURIComponent(sessionId)}/identity-manifests`,
    signal ? { signal } : {},
  );
  return parseIdentityManifestHistory(response);
}

export async function fetchIdentityInspector(
  request: {
    sessionId: string;
    identityId: string;
    manifestVersion: number;
    identityRunId: string;
  },
  signal?: AbortSignal,
): Promise<IdentityInspector> {
  const query = new URLSearchParams({
    manifestVersion: String(request.manifestVersion),
    run: request.identityRunId,
  });
  const response = await dashboardRequest<unknown>(
    `/video-sessions/${encodeURIComponent(request.sessionId)}/identities/${encodeURIComponent(request.identityId)}?${query.toString()}`,
    signal ? { signal } : {},
  );
  return parseIdentityInspector(response, request);
}

export async function fetchIdentityReport(
  artifact: SignedIdentityArtifact,
  expected: {
    sessionId: string;
    manifestVersion: number;
    manifestDigest: string;
    identityRunId: string;
  },
  signal?: AbortSignal,
): Promise<IdentityReport> {
  if (artifact.kind !== "identity_report") {
    throw new IdentityArtifactFetchError("identity_report_artifact_invalid");
  }
  const bytes = await fetchIdentityArtifactBytes(artifact, signal);
  let parsed: unknown;
  try {
    parsed = JSON.parse(
      new TextDecoder("utf-8", { fatal: true }).decode(bytes),
    );
  } catch {
    throw new IdentityArtifactFetchError("identity_report_json_invalid");
  }
  return parseIdentityReport(parsed, expected);
}

export function useIdentityManifestHistory(
  sessionId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["identity-workbench", "manifests", sessionId],
    queryFn: ({ signal }) => fetchIdentityManifestHistory(sessionId, signal),
    enabled,
    retry: retryDashboard,
    staleTime: 15_000,
    gcTime: 0,
  });
}

export function useIdentityInspector(
  request: {
    sessionId: string;
    identityId: string;
    manifestVersion: number;
    identityRunId: string;
  } | null,
  refreshEpoch: number,
) {
  return useQuery({
    queryKey: [
      "identity-workbench",
      "inspector",
      request?.sessionId,
      request?.identityId,
      request?.manifestVersion,
      request?.identityRunId,
      refreshEpoch,
    ],
    queryFn: ({ signal }) => fetchIdentityInspector(request!, signal),
    enabled: request != null,
    retry: retryDashboard,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useIdentityReport(
  artifact: SignedIdentityArtifact | null,
  expected: {
    sessionId: string;
    manifestVersion: number;
    manifestDigest: string;
    identityRunId: string;
  } | null,
  refreshEpoch: number,
) {
  return useQuery({
    queryKey: [
      "identity-workbench",
      "report",
      artifact?.artifact_id,
      artifact?.content_digest,
      refreshEpoch,
    ],
    queryFn: ({ signal }) => fetchIdentityReport(artifact!, expected!, signal),
    enabled: artifact != null && expected != null,
    retry: false,
    gcTime: 0,
  });
}

export function useIdentityContactSheet(
  artifact: SignedIdentityArtifact | null,
  refreshEpoch: number,
) {
  return useQuery({
    queryKey: [
      "identity-workbench",
      "contact-sheet",
      artifact?.artifact_id,
      artifact?.content_digest,
      refreshEpoch,
    ],
    queryFn: ({ signal }) => fetchIdentityArtifactBytes(artifact!, signal),
    enabled: artifact?.kind === "identity_contact_sheet",
    retry: false,
    gcTime: 0,
  });
}
